import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import classes from "./ImportTransactions.module.css";
import Header from "../../Components/Header/Header";
import Sidebar from "../../Components/Sidebar/Sidebar";
import {
    ChevronLeft,
    ChevronRight,
    // AlertCircle,
    // FileText,
    // Database,
    ArrowLeft,
    Search,
    Filter,
    CheckCircle,
    XCircle
} from "lucide-react";

// Import your custom icons from Assets
import TotalRowsIcon from "../../Assets/house.png";
import ValidRowsIcon from "../../Assets/tick.png";
import InvalidRowsIcon from "../../Assets/tick.png";
import DuplicateRowsIcon from "../../Assets/warning.png";
import CheckIcon from "../../Assets/mark.png";

const ImportTransactions = () => {
    const navigate = useNavigate();
    const [currentStep, setCurrentStep] = useState(3);
    const [currentPage, setCurrentPage] = useState(1);
    const [rowsPerPage, setRowsPerPage] = useState(100);
    const [searchTerm, setSearchTerm] = useState("");
    const rowsPerPageOptions = [10, 25, 50, 100];

    const steps = [
        {
            id: 1,
            label: "Upload File",
            description: "File uploaded",
            completed: true,
            number: 1
        },
        {
            id: 2,
            label: "Map Columns",
            description: "Columns mapped",
            completed: true,
            number: 2
        },
        {
            id: 3,
            label: "Preview Data",
            description: "Review and validate data",
            completed: false,
            active: true,
            number: 3
        },
        {
            id: 4,
            label: "Import Summary",
            description: "Complete the import",
            completed: false,
            number: 4
        }
    ];

    const stats = [
        {
            label: "Total Rows",
            value: "1,250",
            subtitle: "Rows in file",
            icon: TotalRowsIcon,
            color: "#6366f1"
        },
        {
            label: "Valid Rows",
            value: "1,242",
            subtitle: "Ready to import",
            icon: ValidRowsIcon,
            color: "#22c55e"
        },
        {
            label: "Invalid Rows",
            value: "8",
            subtitle: "Will be skipped",
            icon: InvalidRowsIcon,
            color: "#ef4444"
        },
        {
            label: "Duplicate Rows",
            value: "0",
            subtitle: "Duplicate found",
            icon: DuplicateRowsIcon,
            color: "#f59e0b"
        },
    ];

    // Generate 100+ rows of transaction data
    const generateTransactionData = () => {
        const data = [];
        const statuses = ["valid", "valid", "valid", "valid", "invalid"];
        const narrations = [
            "Payment from Customer A",
            "Transfer to Supplier B",
            "Customer Payment",
            "Payroll Run",
            "Payment to Vendor C",
            "Bank Charges",
            "Interest Received",
            "Standing Order",
            "Refund to Customer",
            "POS Purchase",
            "Salary Payment",
            "Utility Bill",
            "Rent Payment",
            "Supplier Payment",
            "Insurance Premium",
            "Loan Repayment"
        ];

        for (let i = 1; i <= 105; i++) {
            const statusIndex = i % statuses.length;
            const narrationIndex = i % narrations.length;
            const randomDebit = i % 3 === 0 ? (Math.random() * 100000 + 1000).toFixed(2) : "-";
            const randomCredit = i % 2 === 0 ? (Math.random() * 100000 + 1000).toFixed(2) : "-";
            const randomBalance = (Math.random() * 2000000 + 100000).toFixed(2);

            data.push({
                reference: `TRF/20260814/${String(i).padStart(3, '0')}`,
                date: `${String(14 - (i % 7)).padStart(2, '0')}-08-26`,
                narration: narrations[narrationIndex],
                debit: randomDebit,
                credit: randomCredit,
                balance: randomBalance,
                valueDate: `${String(14 - (i % 7)).padStart(2, '0')}-08-2026`,
                status: statuses[statusIndex]
            });
        }
        return data;
    };

    const allTransactionsData = generateTransactionData();

    // Filter data based on search
    const filteredData = allTransactionsData.filter(item =>
        item.reference.toLowerCase().includes(searchTerm.toLowerCase()) ||
        item.narration.toLowerCase().includes(searchTerm.toLowerCase()) ||
        item.valueDate.includes(searchTerm)
    );

    // Pagination logic
    const totalTransactions = filteredData.length;
    const totalPages = Math.ceil(totalTransactions / rowsPerPage);
    const startIndex = (currentPage - 1) * rowsPerPage;
    const endIndex = Math.min(startIndex + rowsPerPage, totalTransactions);
    const currentTransactions = filteredData.slice(startIndex, endIndex);

    // Mapping data - 6 items in 3 rows of 2 columns
    const mappingData = [
        { from: "Type", to: "Transaction Type" },
        { from: "Date", to: "Transaction Date" },
        { from: "Amount", to: "Amount" },
        { from: "Narration", to: "Narration" },
        { from: "Value Date", to: "Value Date" },
        { from: "Reference", to: "Reference" },
    ];

    // Validation data - 5 items listed vertically
    const validationData = [
        { label: "Date format", status: "valid", message: "All dates are valid" },
        { label: "Reference format", status: "invalid", message: "8 invalid reference(s)" },
        { label: "Required fields", status: "valid", message: "No missing values" },
        { label: "Duplicate rows", status: "valid", message: "No duplicate found" },
        { label: "Amount format", status: "valid", message: "All amounts are valid" },
    ];

    const handleBackClick = () => {
        navigate("/ImportTransaction2");
    };

    const handleNextClick = () => {
        navigate("/ImportTransaction4");
    };

    const handlePageChange = (page) => {
        if (page >= 1 && page <= totalPages) {
            setCurrentPage(page);
        }
    };

    const handleRowsPerPageChange = (e) => {
        setRowsPerPage(Number(e.target.value));
        setCurrentPage(1);
    };

    const getPageNumbers = () => {
        const pages = [];
        const maxVisible = 5;

        if (totalPages <= maxVisible) {
            for (let i = 1; i <= totalPages; i++) {
                pages.push(i);
            }
        } else {
            pages.push(1);

            let start = Math.max(2, currentPage - 1);
            let end = Math.min(totalPages - 1, currentPage + 1);

            if (currentPage <= 3) {
                end = 4;
            }
            if (currentPage >= totalPages - 2) {
                start = totalPages - 3;
            }

            if (start > 2) {
                pages.push('...');
            }

            for (let i = start; i <= end; i++) {
                pages.push(i);
            }

            if (end < totalPages - 1) {
                pages.push('...');
            }

            pages.push(totalPages);
        }

        return pages;
    };

    return (
        <div className={classes.page}>
            <Header />
            <Sidebar />

            <main className={classes.main}>
                <div className={classes.container}>
                    {/* Header Section */}
                    <div className={classes.headerSection}>
                        <div>
                            <h1 className={classes.title}>Import Transactions</h1>
                            <p className={classes.subtitle}>Import bank statement or transaction files into the system</p>
                        </div>
                    </div>

                    {/* Step Indicator */}
                    <div className={classes.stepsContainer}>
                        {steps.map((step, index) => (
                            <React.Fragment key={step.id}>
                                <div className={classes.stepWrapper}>
                                    <div className={`${classes.stepCircle} ${step.completed ? classes.stepCompleted : ''} ${step.active ? classes.stepActive : ''}`}>
                                        {step.completed ? (
                                            <img src={CheckIcon} alt="completed" className={classes.stepIcon} />
                                        ) : (
                                            <span className={classes.stepNumber}>{step.number}</span>
                                        )}
                                    </div>
                                    <div className={classes.stepContent}>
                                        <div className={`${classes.stepLabel} ${step.active ? classes.stepLabelActive : ''} ${step.completed ? classes.stepLabelCompleted : ''}`}>
                                            {step.label}
                                        </div>
                                        <div className={classes.stepDescription}>{step.description}</div>
                                    </div>
                                </div>
                                {index < steps.length - 1 && (
                                    <div className={`${classes.stepLine} ${step.completed ? classes.stepLineCompleted : ''}`}></div>
                                )}
                            </React.Fragment>
                        ))}
                    </div>

                    {/* Preview Data Section with Step 3 of 4 on the same line */}
                    <div className={classes.previewSection}>
                        <div className={classes.previewHeader}>
                            <h2 className={classes.sectionTitle}>Preview Data</h2>
                            <span className={classes.stepIndicator}>Step 3 of 4</span>
                        </div>
                        <p className={classes.sectionSubtitle}>Review the data below to ensure it looks correct before importing</p>
                    </div>

                    {/* Stats Cards with Custom Icons */}
                    <div className={classes.statsGrid}>
                        {stats.map((stat, index) => (
                            <div key={index} className={classes.statCard}>
                                <div className={classes.statIcon}>
                                    <img src={stat.icon} alt={stat.label} className={classes.statImage} />
                                </div>
                                <div className={classes.statInfo}>
                                    <div className={classes.statLabel}>{stat.label}</div>
                                    <div className={classes.statValue}>{stat.value}</div>
                                    <div className={classes.statSubtitle}>{stat.subtitle}</div>
                                </div>
                            </div>
                        ))}
                    </div>

                    {/* Table Toolbar */}
                    <div className={classes.tableToolbar}>
                        <div className={classes.toolbarLeft}>
                            <span className={classes.toolbarLabel}>Show</span>
                            <select
                                className={classes.rowsSelect}
                                value={rowsPerPage}
                                onChange={handleRowsPerPageChange}
                            >
                                {rowsPerPageOptions.map(option => (
                                    <option key={option} value={option}>{option}</option>
                                ))}
                            </select>
                            <span className={classes.toolbarLabel}>rows</span>
                        </div>
                        <div className={classes.toolbarRight}>
                            <div className={classes.toolbarSearch}>
                                <Search size={16} className={classes.toolbarSearchIcon} />
                                <input
                                    type="text"
                                    placeholder="Search in preview data"
                                    className={classes.toolbarSearchInput}
                                    value={searchTerm}
                                    onChange={(e) => {
                                        setSearchTerm(e.target.value);
                                        setCurrentPage(1);
                                    }}
                                />
                            </div>
                            <button className={classes.toolbarMoreFilters}>
                                <Filter size={16} />
                                More Filters
                            </button>
                        </div>
                    </div>

                    {/* Table */}
                    <div className={classes.tableContainer}>
                        <div className={classes.tableWrapper}>
                            <table className={classes.table}>
                                <thead>
                                    <tr>
                                        <th>Reference / Date</th>
                                        <th>Narration</th>
                                        <th>Debit (₦)</th>
                                        <th>Credit (₦)</th>
                                        <th>Balance (₦)</th>
                                        <th>Value Date</th>
                                        <th>Status</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {currentTransactions.length > 0 ? (
                                        currentTransactions.map((item, index) => (
                                            <tr key={index}>
                                                <td>
                                                    <div className={classes.reference}>{item.reference}</div>
                                                    <div className={classes.transDate}>{item.date}</div>
                                                </td>
                                                <td className={classes.narration}>{item.narration}</td>
                                                <td className={item.debit === "-" ? classes.emptyValue : classes.debitValue}>
                                                    {item.debit}
                                                </td>
                                                <td className={item.credit === "-" ? classes.emptyValue : classes.creditValue}>
                                                    {item.credit}
                                                </td>
                                                <td className={classes.balanceValue}>{item.balance}</td>
                                                <td className={classes.valueDate}>{item.valueDate}</td>
                                                <td>
                                                    <span className={`${classes.statusBadge} ${item.status === "valid" ? classes.statusValid : classes.statusInvalid}`}>
                                                        {item.status}
                                                    </span>
                                                </td>
                                            </tr>
                                        ))
                                    ) : (
                                        <tr>
                                            <td colSpan="7" className={classes.noData}>
                                                No transactions found
                                            </td>
                                        </tr>
                                    )}
                                </tbody>
                            </table>
                        </div>
                        {/* Pagination */}
                        <div className={classes.tablePagination}>
                            <span className={classes.paginationInfo}>
                                Showing {totalTransactions > 0 ? startIndex + 1 : 0} to {endIndex} of {totalTransactions} transactions
                            </span>
                            <div className={classes.paginationControls}>
                                <button
                                    className={classes.pageButton}
                                    onClick={() => handlePageChange(currentPage - 1)}
                                    disabled={currentPage === 1 || totalPages === 0}
                                >
                                    Previous
                                </button>
                                {getPageNumbers().map((page, index) => (
                                    <button
                                        key={index}
                                        className={`${classes.pageButton} ${page === currentPage ? classes.activePage : ''}`}
                                        onClick={() => typeof page === 'number' && handlePageChange(page)}
                                        disabled={page === '...'}
                                    >
                                        {page}
                                    </button>
                                ))}
                                <button
                                    className={classes.pageButton}
                                    onClick={() => handlePageChange(currentPage + 1)}
                                    disabled={currentPage === totalPages || totalPages === 0}
                                >
                                    Next
                                </button>
                            </div>
                        </div>
                    </div>

                    {/* Column Mapping Summary - Two Boxes Side by Side */}
                    <div className={classes.mappingWrapper}>
                        {/* Box 1: Column Mapping */}
                        <div className={classes.mappingBox}>
                            <h3 className={classes.mappingTitle}>Column Mapping Summary</h3>

                            <div className={classes.mappingGrid}>
                                {mappingData.map((item, index) => (
                                    <div key={index} className={classes.mappingItem}>
                                        <span className={classes.mappingFrom}>{item.from}</span>
                                        <span className={classes.mappingArrow}>→</span>
                                        <span className={classes.mappingTo}>{item.to}</span>
                                    </div>
                                ))}
                            </div>

                            <div className={classes.mappingFooter}>
                                <button className={classes.viewMappingBtn}>
                                    <ArrowLeft size={16} />
                                    View / Edit Mapping
                                </button>
                            </div>
                        </div>

                        {/* Box 2: Validation Summary - Vertical List */}
                        <div className={classes.validationBox}>
                            <h3 className={classes.mappingTitle}>Validation Summary</h3>

                            <div className={classes.validationList}>
                                {validationData.map((item, index) => (
                                    <div key={index} className={classes.validationItem}>
                                        <div className={classes.validationLabel}>{item.label}</div>
                                        <div className={classes.validationStatus}>
                                            {item.status === 'valid' ? (
                                                <CheckCircle size={14} className={classes.validationIconValid} />
                                            ) : (
                                                <XCircle size={14} className={classes.validationIconInvalid} />
                                            )}
                                            <span className={item.status === 'valid' ? classes.validationTextValid : classes.validationTextInvalid}>
                                                {item.message}
                                            </span>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>
                    </div>

                    {/* Navigation Buttons */}
                    <div className={classes.navigationButtons}>
                        <button
                            className={classes.backButton}
                            onClick={handleBackClick}
                        >
                            <ChevronLeft size={18} />
                            Back: Map Columns
                        </button>
                        <button
                            className={classes.nextButton}
                            onClick={handleNextClick}
                        >
                            Next: Import Summary
                            <ChevronRight size={18} />
                        </button>
                    </div>
                </div>
            </main>
        </div>
    );
};

export default ImportTransactions;